<template>
  <!-- <div class="absolute top-3">SAPEE</div> -->
  <div class="step-page-sheet">
    <Transition
      mode="out-in"
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <StepLoadingSkeleton v-if="isLoading" key="loading" variant="subscriptions" />
      <div v-else key="content" class="p-6 lg:px-8 lg:pt-4 lg:pb-8">
      <OrderPaidBanner :lock-reason="lockReason" />
      <TransitionRoot appear :show="isOpen" as="template">
        <Dialog
          as="div"
          @close="closeModal"
          :open="isOpen"
          v-model="selectedProduct"
        >
          <div
            class="fixed flex justify-center inset-0 z-30 overflow-y-auto bg-black bg-opacity-70"
          >
            <div
              class="h-screen w-screen px-4 flex justify-center items-center"
            >
              <TransitionChild
                as="template"
                enter="duration-400 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-400 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <DialogOverlay class="fixed inset-0" />
              </TransitionChild>

              <span class="inline-block mt-20 align-middle" aria-hidden="true">
                &#8203;
              </span>

              <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
              >
                <div
                  class="lg:leading-loose text-sm lg:text-base h-min w-screen max-w- p-6 overflow-hidden text-left 
                  transition-all transform bg-gray-100 shadow-xl rounded-lg flex flex-col lg:max-w-xl lg:p-6 absolute bottom-0 mobile:bottom-auto"
                >
                  <!-- Close button -->
                  <button
                    @click="closeModal()"
                    class="absolute top-1 right-1 w-8 h-8 flex items-center justify-center text-gray-400 rounded-full focus:outline-none"
                    aria-label="Cerrar modal"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>

                  <DialogTitle
                    as="h3"
                    class="px-2 mt-2 text-lg font-medium text-gray-900 text-center w-full bg-light-gray rounded-lg"
                  >
                    {{ selectedProduct.ecommerceName }}
                  </DialogTitle>

                  <!-- Simple image -->
                  <div
                    v-if="!selectedProduct?.isMultipleProducts"
                    class="mt-2 flex-grow flex justify-center lg:my-4 h-full py-4"
                  >
                    <div class="flex flex-col w-64">
                      <img
                        v-if="!!selectedProduct.image"
                        :src="selectedProduct.image"
                        loading="lazy"
                        class="my-3 -mt-3 object-contain h-60"
                      />
                      <p
                        v-if="selectedProduct.description"
                        class="pl-1 text-sm text-gray-600 max-h-40 overflow-y-auto"
                      >
                        {{ selectedProduct.description }}
                      </p>
                    </div>
                  </div>
                  <!-- Multiple Images -->
                  <div
                    v-else-if="selectedProduct?.isMultipleProducts"
                    class="flex flex-col relative"
                  >
                    <!-- Desktop Navigation Arrows -->
                    <div>
                      <!-- Left Arrow -->
                      <button
                        v-if="canScrollLeft"
                        @click="goToImage(currentImageIndex - 1)"
                        class="absolute left-2 top-1/4 transform -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 focus:outline-none"
                      >
                        <svg
                          class="w-5 h-5 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </button>

                      <!-- Right Arrow -->
                      <button
                        v-if="canScrollRight"
                        @click="goToImage(currentImageIndex + 1)"
                        class="absolute right-2 top-1/4 transform -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 focus:outline-none"
                      >
                        <svg
                          class="w-5 h-5 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </button>
                    </div>

                    <div
                      class="carousel-container"
                      @scroll="handleImageScroll"
                      @wheel="handleWheelScroll"
                      @touchstart="handleTouchStart"
                      @touchend="handleTouchEnd"
                      ref="imageContainer"
                    >
                      <div
                        v-for="p in multipleImages"
                        :key="p.image"
                        class="carousel-item"
                      >
                        <div class="flex flex-col w-full">
                          <img
                            v-if="!!p.image"
                            :src="p.image"
                            loading="lazy"
                            class="w-full h-auto py-4 object-contain max-h-60"
                          />
                          <h1 class="text-center text-lg font-semibold">
                            {{ p.ecommerceName }}
                          </h1>
                          <p
                            v-if="p.description"
                            class="text-xs text-gray-600 max-h-40 overflow-y-auto my-4 pr-1"
                          >
                            {{ p.description }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Dot indicators -->
                    <div
                      class="flex justify-center items-center gap-2 mt-4 mb-2"
                    >
                      <div
                        v-for="(p, index) in multipleImages"
                        :key="`dot-${index}`"
                        :class="[
                          'w-2 h-2 rounded-full transition-all duration-300 cursor-pointer',
                          index === currentImageIndex
                            ? 'bg-aces scale-125'
                            : 'bg-gray-300',
                        ]"
                        @click="goToImage(index)"
                      ></div>
                    </div>
                  </div>

                  <div
                    class="my-3 rounded-lg items-center flex justify-between bg-light-gray"
                  >
                    <div class="mx-4 p-1 font-semibold  lg:p-2 truncate">
                      Unidades
                      <i class="font-light"
                        >(${{ selectedProduct.unitPrice }})</i
                      >
                    </div>
                    <div
                      v-if="effectiveIsEditable"
                      class="p-1 flex flex-row h-full rounded-lg w-24 bg-dark-gray text-white lg:p-2"
                    >
                      <button
                        class="w-1/3 cursor-pointer focus:outline-none "
                        @click="changeQuantity('-')"
                      >
                        <span class="m-auto text-base font-semibold">−</span>
                      </button>
                      <input
                        v-model="selectedProduct.quantity"
                        type="number"
                        class="bg-dark-gray text-white outline-none focus:outline-none text-center w-1/3 font-semibold flex items-center"
                        readonly
                      />
                      <button
                        class=" w-1/3 cursor-pointer focus:outline-none"
                        @click="changeQuantity('+')"
                      >
                        <span class="m-auto text-base font-semibold">+</span>
                      </button>
                    </div>
                    <div
                      v-else
                      class="p-1 flex flex-row h-full rounded-lg w-24 bg-dark-gray text-white lg:p-2"
                    >
                      <button
                        class="w-1/3 cursor-disabled focus:outline-none cursor-not-allowed"
                        disabled
                      >
                        <span class="m-auto text-base font-semibold">−</span>
                      </button>
                      <input
                        v-model="selectedProduct.quantity"
                        type="number"
                        class="bg-dark-gray text-white outline-none focus:outline-none text-center w-1/3 font-semibold md:text-base flex items-center cursor-not-allowed"
                        readonly
                      />
                      <button
                        class=" w-1/3 cursor-disabled focus:outline-none cursor-not-allowed"
                        disabled
                      >
                        <span class="m-auto text-base font-semibold">+</span>
                      </button>
                    </div>
                  </div>

                  <!-- Buttons -->
                  <div v-if="!closingModal">
                    <div
                      class="btn-primary truncate"
                      disabled
                      v-if="disabledCloseButton"
                    >
                      <span v-if="selectedProduct.quantity > 0">
                        Agregar
                        <i class="font-light px-2">
                          (${{
                            selectedProduct.quantity *
                              selectedProduct.unitPrice
                          }})
                        </i>
                      </span>
                      <span v-else>
                        Volver
                      </span>
                    </div>
                    <button
                      v-else-if="
                        effectiveIsEditable &&
                          selectedProduct.quantity != 0 &&
                          !disabledCloseButton
                      "
                      type="button"
                      class="btn-primary truncate"
                      @click="closeModal()"
                    >
                      <span>
                        Agregar
                        <i class="font-light px-2">
                          (${{
                            selectedProduct.quantity *
                              selectedProduct.unitPrice
                          }})
                        </i>
                      </span>
                    </button>

                    <button
                      v-if="
                        selectedProduct.quantity == 0 &&
                          effectiveIsEditable &&
                          !disabledCloseButton
                      "
                      type="button"
                      class="btn-primary truncate"
                      @click="closeModal()"
                    >
                      Volver
                    </button>

                    <button
                      v-else-if="!effectiveIsEditable"
                      type="button"
                      class="btn-neutral truncate"
                      @click="isOpen = false"
                    >
                      Volver
                    </button>

                    <!-- Warning Banner for non-editable orders -->
                    <div
                      v-if="!effectiveIsEditable"
                      class="mt-3 px-3 py-1 bg-red-100 border border-red-200 rounded-lg flex items-center justify-center"
                    >
                      <svg
                        class="w-5 h-5 text-red-600 mr-2 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <span class="text-sm text-red-800 font-medium">
                        Ya no es posible modificar su pedido
                      </span>
                    </div>
                  </div>
                  <div v-else>
                    <div
                      class="loader my-2 mx-auto ease-linear rounded-full border-8 border-t-8 h-8 w-8 lg:h-10 lg:w-10"
                    ></div>
                  </div>
                </div>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
      <p class="font-medium text-center text-dark-gray mb-4 lg:text-base lg:mb-6">
        Elige los materiales que desees para realizar tu pedido de suscripción
      </p>
      <div
        class="sticky top-0 z-20 pb-1 mb-4 -mx-6 px-6 lg:-mx-8 lg:px-8 bg-white border-b border-light-gray"
      >
        <div class="flex flex-row gap-1 whitespace-nowrap overflow-x-auto py-2">
          <button
            v-for="cat in subscriptionCategories"
            :key="cat.categoryId"
            :class="[
              cat.categoryName == selectedCategory
                ? 'bg-aces text-white font-semibold'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            ]"
            class="px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 focus:outline-none flex-shrink-0"
            @click="goto(cat.categoryName)"
          >
            {{ cat.categoryName }}
          </button>
        </div>
      </div>
      <div class="w-full flex flex-col gap-3 pb-24 lg:pb-20 mx-auto h-full">
        <div v-for="cat in subscriptionCategories" :key="cat.categoryId">
          <div class="focus:bg-black" :ref="cat.categoryName">
            <h2 class="px-1 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {{ cat.categoryName }}
            </h2>
            <!-- v-show="prod.categoryId == cat.categoryId" -->
            <div
              v-for="prod in sortBySequence(subscriptionProducts.filter(
                (x) => x.categoryId == cat.categoryId
              ))"
              :key="prod.productId"
              class="py-2"
            >
              <div
                :class="prod.quantity > 0 ? 'border-aces shadow-sm' : 'border-light-gray'"
                class="overflow-hidden rounded-lg border bg-white transition-all duration-200"
              >
                <!-- Fila superior: imagen + texto (clickeable) -->
                <div class="flex cursor-pointer" @click="openModal(prod.productId)">
                  <div v-if="prod.image" class="w-32 flex-shrink-0 self-stretch">
                    <img :src="prod.image" loading="lazy" class="h-full w-full object-cover object-center" />
                  </div>
                  <div class="min-w-0 flex-1 p-3">
                    <p class="truncate font-semibold text-dark-gray">{{ prod.ecommerceName }}</p>
                    <p v-if="prod.description" class="line-clamp-2 text-sm text-gray-500">{{ prod.description }}</p>
                    <p class="mt-2 font-bold text-dark-gray">${{ prod.unitPrice.toLocaleString() }}</p>
                  </div>
                </div>

                <!-- Barra de controles: ancho completo de la tarjeta -->
                <div
                  v-if="effectiveIsEditable"
                  class="flex items-center justify-between border-t border-gray-100 px-3 py-2"
                >
                  <span v-if="prod.quantity > 0" class="text-xs font-medium text-aces">
                    ✓ {{ prod.quantity }} en tu pedido · ${{ (prod.quantity * prod.unitPrice).toLocaleString() }}
                  </span>
                  <span v-else class="text-xs text-gray-400">Tocá para ver detalles</span>

                  <button
                    v-if="prod.quantity === 0"
                    class="rounded-lg bg-aces px-3 py-1 text-sm font-semibold text-white"
                    @click.stop="addOne(prod.productId)"
                  >
                    + Agregar
                  </button>
                  <div v-else class="flex items-center gap-1 rounded-lg bg-gray-100 px-1">
                    <button
                      class="flex h-7 w-7 items-center justify-center text-lg font-bold text-dark-gray focus:outline-none"
                      @click.stop="removeOne(prod.productId)"
                    >−</button>
                    <span class="w-5 text-center text-sm font-semibold text-dark-gray">{{ prod.quantity }}</span>
                    <button
                      class="flex h-7 w-7 items-center justify-center text-lg font-bold text-dark-gray focus:outline-none"
                      @click.stop="addOne(prod.productId)"
                    >+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="fixed flex flex-row items-center justify-around gap-1 bottom-6 inset-x-1/2 transform -translate-x-1/2 px-6 py-2 text-center text-lg font-medium text-white bg-aces rounded-lg lg:text-lg focus:outline-none truncate w-max z-20 disabled:opacity-70"
          :disabled="isCheckingOut"
          @click="goToCart"
        >
          <svg
            v-if="!isCheckingOut"
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="shopping-cart"
            class="svg-inline--fa fa-shopping-cart fa-w-18 mr-2 h-4 lg:h-6"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
          >
            <path
              fill="white"
              d="M528.12 301.319l47.273-208C578.806 78.301 567.391 64 551.99 64H159.208l-9.166-44.81C147.758 8.021 137.93 0 126.529 0H24C10.745 0 0 10.745 0 24v16c0 13.255 10.745 24 24 24h69.883l70.248 343.435C147.325 417.1 136 435.222 136 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-15.674-6.447-29.835-16.824-40h209.647C430.447 426.165 424 440.326 424 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-22.172-12.888-41.332-31.579-50.405l5.517-24.276c3.413-15.018-8.002-29.319-23.403-29.319H218.117l-6.545-32h293.145c11.206 0 20.92-7.754 23.403-18.681z"
            ></path>
          </svg>
          <svg
            v-else
            class="animate-spin h-4 lg:h-5 mr-2 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span class="font-semibold">Ver Carrito</span>
          <i class="font-light text-base">
            (${{ parseFloat(total).toLocaleString() }})
          </i>
        </button>
      </div>
      </div>
    </Transition>
  </div>
</template>

<script>
  import axios from "axios";
  import StepLoadingSkeleton from "./StepLoadingSkeleton.vue";
  import OrderPaidBanner from "./OrderPaidBanner.vue";
  import { ref, watchEffect, watch, inject, triggerRef } from "vue";
  import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  } from "@headlessui/vue";

  export default {
    components: {
      StepLoadingSkeleton,
      OrderPaidBanner,
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
    },
    setup(props, { emit }) {
      const isOpen = ref(false);
      const isLoading = ref(true);
      const isCheckingOut = ref(false);
      const closingModal = ref(false);
      const subscriptionCategories = ref([]);
      const subscriptionProducts = ref([]);
      const selectedProduct = ref(null);

      const sortBySequence = (products) =>
        [...products].sort((a, b) => {
          if (a.sequenceVisualization == null && b.sequenceVisualization == null) return 0;
          if (a.sequenceVisualization == null) return 1;
          if (b.sequenceVisualization == null) return -1;
          return a.sequenceVisualization - b.sequenceVisualization;
        });
      const multipleImages = ref([]);
      const total = ref(0);
      const orderId = ref(props.orderId);
      const effectiveIsEditable = ref(props.isEditable);
      const disabledCloseButton = ref(false);
      const currentImageIndex = ref(0);
      const canScrollLeft = ref(false);
      const canScrollRight = ref(false);
      const imageContainer = ref(null);
      const touchStartX = ref(0);
      const globalState = inject("globalState");

      const initialFetch = async () => {
        isLoading.value = true;
        try {
          if (props.orderId == null && !props.isEditable) {
            const { data: statusData } = await axios.get(
              `Orders/status?churchClientId=${props.authenticatedUser.churchClientId}&clientId=${props.authenticatedUser.clientId}`
            );
            const editableOrder = (statusData.orders || []).find((o) => o.isEditable);
            if (editableOrder) {
              orderId.value = editableOrder.orderId;
              effectiveIsEditable.value = true;
              emit("orderResolved", {
                orderId: editableOrder.orderId,
                isEditable: true,
                lockReason: editableOrder.lockReason || null,
              });
            } else if (statusData.canCreateNew) {
              effectiveIsEditable.value = true;
              emit("orderResolved", { orderId: null, isEditable: true, lockReason: null });
            }
          }
          const { data: categories } = await axios.get(`Categories/all`);
          subscriptionCategories.value = categories;
          await fetchCartDB();
        } catch (err) {
          console.log(err.response);
        } finally {
          isLoading.value = false;
        }
      };
      // ✅ Vuelve a llamar a getprovinces si se actualiza el token
      watch(
        () => globalState.tokenUpdated,
        (newVal) => {
          if (newVal === true) {
            console.log("Token actualizado. Reconsultando carrito...");
            initialFetch();
            globalState.tokenUpdated = false; // Reinicia la bandera para futuras actualizaciones
          }
        }
      );
      const fetchCartDB = async () => {
        const {
          data: { periodId },
        } = await axios.get("Periods/getCurrent");

        const res = await axios.get(
          `Products/byOrder?periodId=${periodId}${
            orderId.value == null ? "" : `&orderId=${orderId.value}`
          }`
        );

        subscriptionProducts.value = res.data;
        return res;
      };

      const checkoutCart = async () => {
        const cart = [
          ...subscriptionProducts.value.filter(
            (x) => x.quantity != 0 || x.orderDetailId != 0
          ),
        ];
        if (cart.length == 0) return;
        isCheckingOut.value = true;
        try {
          const isNewOrder = orderId.value == null;
          const res = await axios
            .put(
              `Orders/upsert?${
                isNewOrder ? "" : `orderId=${orderId.value}&`
              }subscriptorId=${props.authenticatedUser.clientId}`,
              cart
            )
            .catch((err) => { console.log(err.response); throw err; });

          if (isNewOrder) {
            const responseOrderId = res.data?.orderId ?? res.data?.OrderId ?? null;
            if (responseOrderId) {
              orderId.value = responseOrderId;
            } else {
              const { data: statusData } = await axios.get(
                `Orders/status?churchClientId=${props.authenticatedUser.churchClientId}&clientId=${props.authenticatedUser.clientId}`
              );
              const editables = (statusData.orders || []).filter((o) => o.isEditable);
              if (editables.length > 0) {
                orderId.value = editables.reduce((max, o) =>
                  o.orderId > max.orderId ? o : max
                ).orderId;
              }
            }
            emit("orderCreated", orderId.value);
          }
        } finally {
          isCheckingOut.value = false;
        }
      };

      let saveTimer = null;
      const debouncedSave = () => {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => {
          saveTimer = null;
          checkoutCart();
        }, 600);
      };
      const cancelDebouncedSave = () => {
        if (saveTimer) {
          clearTimeout(saveTimer);
          saveTimer = null;
        }
      };

      watchEffect(() => {
        const t = subscriptionProducts.value
          .filter((x) => x.amount != 0)
          .reduce((acc, curr) => acc + curr.amount, 0);
        total.value = t;
      });
      watchEffect(async () => {
        if (selectedProduct.value?.isMultipleProducts == 1) {
          await axios
            .get(
              `Products/related?productId=${selectedProduct.value.productId}`
            )
            .then((res) => {
              multipleImages.value = res.data;
              currentImageIndex.value = 0;
              updateScrollIndicators();
            })
            .catch((err) => console.log(err.response));
        }
      });

      const updateScrollIndicators = () => {
        if (multipleImages.value.length > 0) {
          canScrollLeft.value = currentImageIndex.value > 0;
          canScrollRight.value =
            currentImageIndex.value < multipleImages.value.length - 1;
        }
      };

      const handleImageScroll = (event) => {
        const container = event.target;
        const scrollLeft = container.scrollLeft;
        const containerWidth = container.clientWidth;

        // Calculate which image is currently most visible
        const newIndex = Math.round(scrollLeft / containerWidth);

        if (
          newIndex !== currentImageIndex.value &&
          newIndex >= 0 &&
          newIndex < multipleImages.value.length
        ) {
          currentImageIndex.value = newIndex;
          updateScrollIndicators();
        }
      };

      const handleWheelScroll = (event) => {
        event.preventDefault();
        const delta = event.deltaX || event.deltaY;

        if (delta > 0 && canScrollRight.value) {
          this.goToImage(currentImageIndex.value + 1);
        } else if (delta < 0 && canScrollLeft.value) {
          this.goToImage(currentImageIndex.value - 1);
        }
      };

      const handleTouchStart = (event) => {
        const touch = event.touches[0];
        touchStartX.value = touch.clientX;
      };

      const handleTouchEnd = (event) => {
        const touch = event.changedTouches[0];
        const touchEndX = touch.clientX;
        const diff = touchStartX.value - touchEndX;
        const threshold = 50; // Minimum distance for swipe

        if (Math.abs(diff) > threshold) {
          if (diff > 0 && canScrollRight.value) {
            this.goToImage(currentImageIndex.value + 1);
          } else if (diff < 0 && canScrollLeft.value) {
            this.goToImage(currentImageIndex.value - 1);
          }
        }
      };

      initialFetch();

      return {
        subscriptionCategories,
        subscriptionProducts,
        sortBySequence,
        selectedProduct,
        total,
        isLoading,
        isOpen,
        closingModal,
        multipleImages,
        disabledCloseButton,
        currentImageIndex,
        canScrollLeft,
        canScrollRight,
        imageContainer,
        handleImageScroll,
        handleWheelScroll,
        handleTouchStart,
        handleTouchEnd,
        effectiveIsEditable,
        isCheckingOut,
        checkoutCart,
        cancelDebouncedSave,
        addOne(productId) {
          const prod = subscriptionProducts.value.find(
            (e) => e.productId === productId
          );
          if (!prod) return;
          prod.quantity += 1;
          prod.amount = prod.unitPrice * prod.quantity;
          triggerRef(subscriptionProducts);
          debouncedSave();
        },
        removeOne(productId) {
          const prod = subscriptionProducts.value.find(
            (e) => e.productId === productId
          );
          if (!prod || prod.quantity === 0) return;
          prod.quantity -= 1;
          prod.amount = prod.unitPrice * prod.quantity;
          triggerRef(subscriptionProducts);
          debouncedSave();
        },
        closeModal() {
          isOpen.value = false;
        },
        openModal(p) {
          isOpen.value = true;
          selectedProduct.value = subscriptionProducts.value.find(
            (e) => e.productId === p
          );
        },
        changeQuantity(operation) {
          if (operation == "-" && selectedProduct.value.quantity !== 0)
            selectedProduct.value.quantity -= 1;
          if (operation == "+") selectedProduct.value.quantity += 1;
          selectedProduct.value.amount =
            selectedProduct.value.unitPrice * selectedProduct.value.quantity;

          const prod = subscriptionProducts.value.find(
            (e) => e.productId === selectedProduct.value.productId
          );
          if (prod) {
            prod.quantity = selectedProduct.value.quantity;
            prod.amount = selectedProduct.value.amount;
          }
          triggerRef(subscriptionProducts);
          debouncedSave();
        },
        goToImage(index) {
          if (index < 0 || index >= multipleImages.value.length) return;

          currentImageIndex.value = index;
          updateScrollIndicators();

          // Scroll to the selected image
          if (imageContainer.value) {
            const container = imageContainer.value;
            const containerWidth = container.clientWidth;
            const scrollPosition = index * containerWidth;

            container.scrollTo({
              left: scrollPosition,
              behavior: "smooth",
            });
          }
        },
      };
    },
    props: {
      authenticatedUser: { type: Object, default: () => ({}) },
      orderId: { type: Number, default: null },
      isEditable: { type: Boolean, default: false },
      lockReason: { type: String, default: null },
    },
    emits: ["changeStep", "orderCreated", "orderResolved"],
    data() {
      return {
        selectedCategory: "",
      };
    },
    created() {
      window.addEventListener("scroll", this.handleScroll);
    },
    unmounted() {
      window.removeEventListener("scroll", this.handleScroll);
    },
    methods: {
      handleScroll() {
        const f = this.subscriptionCategories.filter(
          (x) =>
            window.scrollY > this.$refs[x.categoryName].offsetTop - 130 &&
            window.scrollY <
              this.$refs[x.categoryName]?.offsetHeight +
                this.$refs[x.categoryName]?.offsetTop -
                90
        );
        this.selectedCategory = f[0]?.categoryName ?? null;
      },
      goto(refName) {
        window.scrollTo({
          top: this.$refs[refName]?.offsetTop - 50,
          behavior: "smooth",
        });
      },
      async goToCart() {
        this.cancelDebouncedSave();
        await this.checkoutCart();
        this.$emit("changeStep", 4);
      },
    },
  };
</script>
<style>
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="submit"],
  button {
    -webkit-appearance: none;
    appearance: none;
  }

  /* Hide horizontal scrollbar but keep scroll functionality */
  .carousel-container {
    display: flex;
    flex-direction: row;
    overflow-x: scroll;
    gap: 0;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }

  .carousel-container::-webkit-scrollbar {
    display: none;
  }

  .carousel-container {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .carousel-item {
    flex-shrink: 0;
    width: 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    margin-top: 0.5rem;
  }

  @media (min-width: 1024px) {
    .carousel-item {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }
  }
</style>
